import type { components } from "@dolfin/api-public";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Address = components["schemas"]["Address"];

/** Editable address as plain (non-null) strings, suited to controlled inputs. */
export interface AddressValue {
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  country: string;
}

export const emptyAddress: AddressValue = {
  line1: "",
  line2: "",
  city: "",
  postalCode: "",
  country: "",
};

export function addressToValue(address: Address | null | undefined): AddressValue {
  return {
    line1: address?.line1 ?? "",
    line2: address?.line2 ?? "",
    city: address?.city ?? "",
    postalCode: address?.postalCode ?? "",
    country: address?.country ?? "",
  };
}

/** Map editable strings back to a request body, trimming and nulling empties. */
export function addressToRequest(value: AddressValue): Address {
  return {
    line1: value.line1.trim() || null,
    line2: value.line2.trim() || null,
    city: value.city.trim() || null,
    postalCode: value.postalCode.trim() || null,
    country: value.country.trim() || null,
  };
}

/** Lines suitable for an inline, comma-free single-block render. */
export function addressLines(address: Address | null | undefined): string[] {
  return [
    address?.line1,
    address?.line2,
    address?.city,
    address?.postalCode,
    address?.country,
  ].filter((part): part is string => Boolean(part));
}

export function hasAddress(address: Address | null | undefined): boolean {
  return addressLines(address).length > 0;
}

export function AddressFields({
  value,
  onChange,
}: {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
}) {
  const set = (patch: Partial<AddressValue>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">
        Address
      </Label>
      <Input
        placeholder="Address line 1"
        value={value.line1}
        onChange={(e) => set({ line1: e.target.value })}
      />
      <Input
        placeholder="Address line 2"
        value={value.line2}
        onChange={(e) => set({ line2: e.target.value })}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input
          placeholder="City"
          value={value.city}
          onChange={(e) => set({ city: e.target.value })}
        />
        <Input
          placeholder="Postal code"
          value={value.postalCode}
          onChange={(e) => set({ postalCode: e.target.value })}
        />
      </div>
      <Input
        placeholder="Country"
        value={value.country}
        onChange={(e) => set({ country: e.target.value })}
      />
    </div>
  );
}

export function AddressDisplay({ address }: { address: Address | null | undefined }) {
  if (!hasAddress(address)) return null;
  return (
    <div className="space-y-2.5">
      <DetailRow label="Line 1" value={address?.line1} />
      <DetailRow label="Line 2" value={address?.line2} />
      <DetailRow label="City" value={address?.city} />
      <DetailRow label="Postal code" value={address?.postalCode} />
      <DetailRow label="Country" value={address?.country} />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
